import { useQuery } from "@tanstack/react-query";
import type { AiAction } from "../../api/hub/ai";
import { callDataTool, type DataToolResult } from "../../api/hub/open";
import type { CalendarItem, Project, Task } from "../../api/hub/types";
import { taskDate } from "../tasks/time";
import { calendarRange, dataKey, dayStart, useWorkspaceMe } from "./data";

/** One page of a canonical list plus the server's own total; total is null when the server did not report it and more pages exist. */
export type Listed<T> = {
  items: T[];
  total: number | null;
  complete: boolean;
  result: DataToolResult<{ items: T[] }>;
};
async function readList<T>(
  name: string,
  args: Record<string, unknown>,
): Promise<Listed<T>> {
  const result = await callDataTool<{ items: T[] }>(name, args);
  if (!result.data || !Array.isArray(result.data.items)) {
    throw new Error("数据格式不正确，请刷新或回到业务页面核对");
  }
  const items = result.data.items, more = !!result.pagination?.hasMore;
  return {
    items,
    total: result.pagination?.total ?? (more ? null : items.length),
    complete: !more,
    result,
  };
}
export function useList<T>(
  name: string,
  args: Record<string, unknown>,
  enabled = true,
) {
  return useQuery({
    queryKey: [...dataKey(name, args), "dashboard"],
    queryFn: () => readList<T>(name, args),
    enabled,
    staleTime: 30_000,
  });
}
export function addDays(day: string, n: number) {
  const date = new Date(day + "T12:00:00Z");
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}
export function daysBetween(from: string, to: string) {
  return Math.round(
    (Date.parse(to + "T12:00:00Z") - Date.parse(from + "T12:00:00Z")) /
      86_400_000,
  );
}
export function weekdayLabel(day: string) {
  return "周" + "日一二三四五六"[new Date(day + "T12:00:00Z").getUTCDay()];
}
/** Calendar days are always the account's timezone, never the browser's. */
export function useClock() {
  const me = useWorkspaceMe();
  const timezone = me.data?.data.timezone ?? "Asia/Shanghai",
    today = taskDate(Date.now(), timezone),
    week = calendarRange("week", timezone),
    days = Array.from({ length: 7 }, (_, i) => addDays(week.from, i));
  return { me, timezone, today, days, ready: me.isSuccess };
}
const OPEN = (task: Task) => !["done", "canceled"].includes(task.status);
export const STAGES = [
  { key: "todo", label: "待办" },
  { key: "in_progress", label: "进行中" },
  { key: "in_review", label: "待验收" },
] as const;
export function useTaskPulse() {
  const { timezone, today, days, ready } = useClock();
  const stage = {
    todo: useList<Task>("tasks_mine", { status: "todo", limit: 1 }),
    in_progress: useList<Task>("tasks_mine", {
      status: "in_progress",
      limit: 1,
    }),
    in_review: useList<Task>("tasks_mine", { status: "in_review", limit: 1 }),
  };
  const done = useList<Task>("tasks_mine", { status: "done", limit: 1 }),
    overdue = useList<Task>("tasks_mine", { overdue: true, limit: 1 }),
    dueWeek = useList<Task>("tasks_mine", {
      from: days[0],
      to: days[6],
      limit: 100,
    }, ready);
  const open = dueWeek.data?.items.filter(OPEN) ?? [];
  return {
    stage,
    done,
    overdue,
    dueWeek,
    dueWeekCount: dueWeek.data?.complete ? open.length : null,
    dueTodayCount: dueWeek.data?.complete
      ? open.filter((task) =>
        task.dueDate && taskDate(task.dueDate, timezone) === today
      )
        .length
      : null,
  };
}
export const HEALTH = [
  { key: "off_track", label: "偏离计划" },
  { key: "at_risk", label: "有风险" },
  { key: "on_track", label: "正常推进" },
] as const;
export function useMyProjects() {
  const { me } = useClock();
  const query = useList<Project>("projects_list", { limit: 100 }, me.isSuccess);
  const id = me.data?.data.id;
  const projects = (query.data?.items ?? [])
    .filter((project) =>
      (!project.status ||
        ["planned", "active", "paused"].includes(project.status)) &&
      (!id || project.ownerId === id || project.memberIds?.includes(id))
    )
    .sort((a, b) =>
      HEALTH.findIndex((h) => h.key === a.health) -
        HEALTH.findIndex((h) => h.key === b.health) ||
      (a.targetDate ?? "9999").localeCompare(b.targetDate ?? "9999")
    );
  return { query, projects, me };
}
export function useWeekAgenda() {
  const { days, ready } = useClock();
  const query = useList<CalendarItem>("calendar_range", {
    from: days[0],
    to: addDays(days[6], 1),
    limit: 100,
  }, ready);
  return { query, days };
}
export const AI_STATES = [
  { key: "applied", label: "已生效" },
  { key: "review", label: "待人核对" },
  { key: "failed", label: "执行失败" },
  { key: "undone", label: "已撤销" },
] as const;
export type AiState = (typeof AI_STATES)[number]["key"];
export function aiState(action: AiAction): AiState {
  if (action.status === "failed") return "failed";
  if (action.undoneAt || action.status === "undone") return "undone";
  if (
    action.status === "needs_review" || action.status === "suggested" ||
    action.status === "pending" || action.status === "undoing"
  ) return "review";
  return "applied";
}
export function useAiWeek() {
  const { timezone, today, ready } = useClock();
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const query = useList<AiAction>("ai_actions_recent", {
    since: dayStart(timezone, new Date(Date.now() - 6 * 86_400_000)),
    limit: 100,
  }, ready);
  return { query, days, timezone };
}
